import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { assetService } from '../../../services/assetService';

const AssetEditModal = ({ asset, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: asset?.title || '',
    description: asset?.description || '',
    category: asset?.category || 'logo',
    campaign: asset?.campaign || '',
    orientation: asset?.orientation || 'landscape'
  });
  const [replacementFile, setReplacementFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file size
    if (file?.size > 52428800) {
      setError('File size must be less than 50MB');
      return;
    }

    setReplacementFile(file);
    setError('');
  };

  const handleSaveMetadata = async (e) => {
    e?.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      
      // If there's a replacement file, replace it first
      if (replacementFile) {
        await assetService?.replace(asset?.id, replacementFile);
      }
      
      // Then update metadata
      await assetService?.update(asset?.id, formData);
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to update asset');
    } finally {
      setSaving(false);
    }
  };

  const handleReplaceFile = async () => {
    if (!replacementFile) {
      setError('Please select a file to replace');
      return;
    }

    if (!window.confirm('Are you sure you want to replace this file? This action cannot be undone.')) {
      return;
    }

    try {
      setReplacing(true);
      setError('');
      await assetService?.replace(asset?.id, replacementFile);
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to replace file');
    } finally {
      setReplacing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Edit Asset</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Asset Preview */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Current Asset
            </label>
            <div className="border border-border rounded-lg p-4 bg-muted">
              {asset?.fileType === 'video' ? (
                <div className="aspect-video flex items-center justify-center">
                  <Icon name="Video" size={64} className="text-muted-foreground" />
                </div>
              ) : (
                <img
                  src={asset?.thumbnail}
                  alt={asset?.title}
                  className="w-full h-auto rounded"
                />
              )}
            </div>
          </div>

          {/* Replace File Section */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="RefreshCw" size={18} />
              Replace File
            </h3>
            <div className="space-y-3">
              <input
                type="file"
                onChange={handleFileChange}
                accept="image/*,video/*,application/pdf"
                className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
              {replacementFile && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <Icon name="CheckCircle" size={18} className="text-green-600" />
                  <span className="text-sm text-foreground flex-1">{replacementFile?.name}</span>
                  <button
                    type="button"
                    onClick={() => setReplacementFile(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              )}
              {replacementFile && (
                <p className="text-xs text-muted-foreground">
                  Click "Save Changes" below to upload this file and update metadata.
                </p>
              )}
            </div>
          </div>

          {/* Metadata Form */}
          <form onSubmit={handleSaveMetadata} className="space-y-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Icon name="Edit" size={18} />
              Edit Metadata
            </h3>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Title *
              </label>
              <Input
                value={formData?.title}
                onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                placeholder="Enter asset title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                placeholder="Enter asset description"
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Category *
              </label>
              <select
                value={formData?.category}
                onChange={(e) => setFormData({ ...formData, category: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                required
              >
                <option value="logo">Logo</option>
                <option value="technology">Technology</option>
                <option value="photo">Photo</option>
                <option value="diagram">Diagram</option>
                <option value="video">Video</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Campaign
              </label>
              <Input
                value={formData?.campaign}
                onChange={(e) => setFormData({ ...formData, campaign: e?.target?.value })}
                placeholder="e.g., Q1 2026 Launch"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Orientation
              </label>
              <select
                value={formData?.orientation}
                onChange={(e) => setFormData({ ...formData, orientation: e?.target?.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
                <option value="square">Square</option>
              </select>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive rounded-lg">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="flex items-center gap-3 pt-4">
              <Button
                type="submit"
                loading={saving}
                disabled={saving}
                iconName="Save"
                iconPosition="left"
                variant="secondary"
                className="flex-1"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={saving || replacing}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssetEditModal;